import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { z } from 'zod';

import { InfoPage } from '@/components/info-page';
import { EntityTable } from '@/components/table/entity-table';
import { TableErrorState } from '@/components/table/table-error-state';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown';
import { TableFilterInputField } from '@/components/table/table-filter-field';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button';
import { StudentStatusBadge } from '@/components/students/student-status-badge';
import { useAllSchools, useOrgOptions, useSchoolAndOrgOptions } from '@/lib/api-call-hooks';
import { EmptyArray } from '@/lib/api-calls';
import { usePaginatedFilters } from '@/lib/filtering';
import { paginationRequestSchema } from '@/lib/pagination';
import { useNavigateSearchState, validateRouteRole } from '@/lib/routing';
import { useSorting } from '@/lib/sorting';
import { studentsTabs } from '../-components/tabs';
import { useTimeZone } from '@/auth/useTimeZone';
import { isAirAdmin } from '@airreading/common';
import { getMakeupDiagnosticStudents } from '@/lib/api-calls';
import { airColumnHelper } from '@/lib/tables';
import { formatInstant, getUpcomingSessions } from '@airreading/common';
import { Temporal } from 'temporal-polyfill';
import { getStudentYearOptions } from '@/lib/student-utils';
import { formatStudentClassTimeAbbr } from '@/routes/_layout/scheduling/schedules/-components/formatting';
import { getDistrictColumn } from '@/lib/district-utils';
import {
  FullStudent,
  GetMakeupDiagnosticStudentsSortableColumn,
  getMakeupDiagnosticStudentsSortOrderSchema,
  getMakeupDiagnosticStudentsSortableColumnSchema,
  studentYearOption,
  studentYearSchema,
  StudentStatus,
  StudentYearColorMap,
} from '@airreading/common/data';

const searchSchema = paginationRequestSchema.extend({
  query: z.string().optional().catch(''),
  schools: z.array(z.string()).optional(),
  orgs: z.array(z.string()).optional(),
  grades: z.array(studentYearSchema).optional(),
  sortBy: getMakeupDiagnosticStudentsSortOrderSchema.optional(),
});

export const Route = createFileRoute('/_layout/students/makeup-diagnostic/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: MakeupDiagnosticStudents,
  validateSearch: searchSchema,
});

const col = airColumnHelper<FullStudent>(getMakeupDiagnosticStudentsSortableColumnSchema);
const buildColumns = (timeZone: string, schoolIdToDistrictName: Record<string, string>) => [
  col.accessor((row) => row.id, {
    id: 'fullNameOrEmail',
    header: 'Name',
    cell: ({ row }) => row.original.fullNameOrEmail,
  }),
  col.accessor('email', {
    id: 'email',
    header: 'Email',
  }),
  col.accessor((row) => row.school, {
    id: 'district',
    header: 'District',
    cell: ({ getValue }) => {
      const school = getValue();
      const districtName = school?.id ? schoolIdToDistrictName[school.id] : null;
      if (!districtName) return <span>Unknown</span>;
      return school
        ? getDistrictColumn({
            orgId: school.orgId,
            orgName: districtName,
          })
        : null;
    },
  }),
  col.accessor((row) => row.school, {
    id: 'school',
    header: 'School',
    enableSorting: false,
    cell: ({ getValue }) => {
      const school = getValue();
      return school ? (
        <Link
          to={'/orgs/schools/$schoolId/classes'}
          params={{ schoolId: school.id }}
          search={{ pageIndex: 0, pageSize: 20 }}
          className="text-blue-600"
        >
          {school.name}
        </Link>
      ) : (
        '-'
      );
    },
  }),
  col.accessor('grade', {
    id: 'grade',
    header: 'Grade',
    cell: ({ getValue }) => {
      const grade = getValue();
      if (!grade) return studentYearOption(grade);
      const backgroundColor = StudentYearColorMap[grade];
      return (
        <div className="min-w-[80px] flex justify-start">
          <span className="text-xs rounded-full px-2 py-1 min-w-9 text-center" style={{ backgroundColor }}>
            {studentYearOption(grade)}
          </span>
        </div>
      );
    },
  }),
  col.accessor('status', {
    id: 'status',
    header: 'Status',
    cell: ({ getValue }) => <StudentStatusBadge status={getValue() as StudentStatus} />,
  }),
  col.accessor((row) => row.requestedClassTime?.schedule?.name ?? '', {
    id: 'schedule',
    header: 'Schedule',
  }),
  col.accessor((row) => (row.requestedClassTime ? formatStudentClassTimeAbbr(row.requestedClassTime) : ''), {
    id: 'classTime',
    header: 'Class time (CT)',
  }),
  col.accessor('learningPath', {
    id: 'learningPath',
    header: 'Learning path',
    cell: ({ getValue }) => getValue()?.name ?? '',
  }),
  col.accessor('startingLesson', {
    id: 'startingLesson',
    header: 'Starting lesson',
    cell: ({ getValue }) => getValue()?.name ?? '',
  }),
  col.accessor(
    (row) => {
      const sessions = row.studentSessions ?? [];
      const futureSessions = sessions
        .filter((session) => session.startInstant.epochMilliseconds >= Temporal.Now.instant().epochMilliseconds)
        .toSorted((a, b) => a.startInstant.epochMilliseconds - b.startInstant.epochMilliseconds);
      return futureSessions[0]?.lesson?.name ?? '';
    },
    { id: 'nextLesson', header: 'Next lesson' },
  ),
  col.accessor('studentSessions', {
    id: 'nextSessionDate',
    header: 'Next session date (CT)',
    cell: ({ getValue }) => {
      const { nextSession } = getUpcomingSessions({ sessions: getValue() ?? [], timeZone });
      return nextSession && formatInstant(nextSession.startInstant, timeZone).formattedDate;
    },
  }),
  col.accessor('totalFutureSessions', {
    id: 'totalFutureSessions',
    header: 'Total of future sessions',
  }),
];

function MakeupDiagnosticStudents() {
  const { timeZone } = useTimeZone();
  const { data: allSchools } = useAllSchools();
  const schoolIdToDistrictName = useMemo(
    () => Object.fromEntries(allSchools.map((school) => [school.id, school.org.name])),
    [allSchools],
  );
  const columns = useMemo(() => buildColumns(timeZone, schoolIdToDistrictName), [timeZone, schoolIdToDistrictName]);

  const [sortBy, didChangeSortBy] = useNavigateSearchState(Route.id, 'sortBy');
  const { sorting, ...tableSortingOptions } = useSorting<GetMakeupDiagnosticStudentsSortableColumn>([
    sortBy,
    didChangeSortBy,
  ]);

  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[userNameQuery, activeUserNameQuery], setUserNameQuery],
      schools: [[selectedSchools, activeSelectedSchools], setSelectedSchools],
      orgs: [[, activeSelectedOrgs], setSelectedOrgs],
      grades: [[selectedGrades, activeSelectedGrades], setSelectedGrades],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, ['query', 'schools', 'orgs', 'grades']);

  const allOrgOptions = useOrgOptions();
  const allSchoolOptions = useSchoolAndOrgOptions({ orgIds: activeSelectedOrgs ?? [] });

  const {
    data: response,
    isPending,
    error,
    refetch,
  } = useQuery(
    getMakeupDiagnosticStudents({
      pageIndex,
      pageSize,
      filters: {
        query: activeUserNameQuery,
        schools: activeSelectedSchools,
        orgs: activeSelectedOrgs,
        grades: activeSelectedGrades,
      },
      sortBy,
    }),
  );

  const students = useMemo(() => response?.students || EmptyArray, [response]);

  const table = useReactTable({
    data: students,
    columns,
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
      sorting,
    },
    rowCount: response?.total || 0,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    manualPagination: true,
    ...tableSortingOptions,
    enableColumnResizing: true,
  });

  const filterLeftComponents = useMemo(
    () => (
      <>
        <TableFilterInputField
          placeholder="Search users by name or email"
          value={userNameQuery ?? ''}
          onChange={(event) => setUserNameQuery(event.target.value)}
        />
        <TableFilterDropdown
          title="Districts"
          options={allOrgOptions}
          selectedValues={activeSelectedOrgs ?? []}
          onChange={setSelectedOrgs}
        />
        <TableFilterDropdown
          title="Schools"
          options={allSchoolOptions}
          selectedValues={selectedSchools ?? []}
          onChange={setSelectedSchools}
        />
        <TableFilterDropdown
          title="Grades"
          options={getStudentYearOptions()}
          selectedValues={selectedGrades ?? []}
          onChange={setSelectedGrades}
        />
        <TableFilterResetButton show={true} onClick={resetFilters} />
      </>
    ),
    [
      userNameQuery,
      setUserNameQuery,
      allOrgOptions,
      activeSelectedOrgs,
      setSelectedOrgs,
      allSchoolOptions,
      selectedSchools,
      setSelectedSchools,
      selectedGrades,
      setSelectedGrades,
      resetFilters,
    ],
  );

  return (
    <InfoPage headerText="Students" tabs={studentsTabs('Makeup diagnostic')}>
      <EntityTable
        table={table}
        showPagination={true}
        isLoading={isPending}
        hasError={!!error}
        emptyState={'No students found'}
        errorState={<TableErrorState refetchAction={refetch} />}
        filters={{ filterLeftComponents }}
      />
    </InfoPage>
  );
}
